/**
 * Display labels for report date ranges and date buckets.
 *
 * Every date here is a date-only "YYYY-MM-DD" string in the salon's timezone.
 * They are parsed as LOCAL calendar days (`parseLocalDate`) and never through
 * `new Date("YYYY-MM-DD")`, which would be UTC midnight and render as the
 * previous day west of UTC.
 */
import moment from "moment";
import { DATE_RANGE_PRESETS, DateRangePreset } from "../types/reports";
import { formatLocalDate, parseLocalDate } from "./localDateUtils";

const PRESET_LABEL = new Map<DateRangePreset, string>(
  DATE_RANGE_PRESETS.map((p) => [p.value, p.label]),
);

const formatDisplay = (iso: string): string =>
  moment(iso, "YYYY-MM-DD").format("MMM D, YYYY");

// Calendar-day moments built from the date's parts. `moment(date)` would
// re-read the instant in moment's default zone, which (once moment-timezone
// has a default set, as in the test setup) can land on the previous day.
const cal = (d: Date): moment.Moment =>
  moment([d.getFullYear(), d.getMonth(), d.getDate()]);
const toDay = (m: moment.Moment): Date =>
  new Date(m.year(), m.month(), m.date());

/** Human label for a preset / custom range selection ("Last 7 days",
 *  "Oct 1, 2026 – Oct 7, 2026"). Used by the reports date Button, the Sales
 *  date pill, and the Reports date chip. */
export function dateRangeLabel(
  preset: DateRangePreset,
  customStart: string | null,
  customEnd: string | null,
): string {
  if (preset === "fixed_dates") {
    return customStart && customEnd
      ? `${formatDisplay(customStart)} – ${formatDisplay(customEnd)}`
      : "Custom range";
  }
  return PRESET_LABEL.get(preset) ?? preset;
}

export type BucketGranularity = "day" | "week" | "month";

export interface BucketRange {
  /** First day of the report's date range ("YYYY-MM-DD"). */
  rangeStart: string;
  /** Last day of the report's date range ("YYYY-MM-DD"). */
  rangeEnd: string;
}

/** Clamp a bucket [start, end] to the report range so a partial first/last
 *  week or month shows the days it actually covers. */
function clampToRange(
  start: Date,
  end: Date,
  { rangeStart, rangeEnd }: BucketRange,
): [Date, Date] {
  const lo = parseLocalDate(rangeStart);
  const hi = parseLocalDate(rangeEnd);
  const from = lo && lo > start ? lo : start;
  const to = hi && hi < end ? hi : end;
  return [from, to];
}

/** "Oct 5 – 11", "Sep 28 – Oct 4", or "Dec 28, 2025 – Jan 3, 2026". */
function formatSpan(from: Date, to: Date): string {
  const a = cal(from);
  const b = cal(to);
  if (a.isSame(b, "day")) return a.format("MMM D, YYYY");
  if (!a.isSame(b, "year")) {
    return `${a.format("MMM D, YYYY")} – ${b.format("MMM D, YYYY")}`;
  }
  if (a.isSame(b, "month")) return `${a.format("MMM D")} – ${b.format("D")}`;
  return `${a.format("MMM D")} – ${b.format("MMM D")}`;
}

/** Resolved dates of a range ("Oct 1 – 7", "Sep 28 – Oct 4", "Oct 7, 2026")
 *  from two "YYYY-MM-DD" strings, e.g. the muted hint on a date chip. */
export function formatDateSpan(start: string, end: string): string {
  const from = parseLocalDate(start);
  const to = parseLocalDate(end);
  if (!from || !to) return "";
  return formatSpan(from, to);
}

/**
 * Label for a date bucket returned by a report run. The API labels each bucket
 * with its START date; the portal formats it for the chosen granularity:
 *
 * - day   → "Tue 10/6/26"
 * - week  → "Oct 4 – 10" (weeks start Sunday; clipped to the report range, so a
 *           partial first/last week shows only the days it covers)
 * - month → "Oct 2026" for a whole month, or "Oct 1 – 7, 2026" when the report
 *           range covers only part of it
 *
 * Unparseable input is returned unchanged rather than throwing.
 */
export function formatBucketLabel(
  isoDate: string,
  granularity: BucketGranularity,
  range: BucketRange,
): string {
  const start = parseLocalDate(isoDate);
  if (!start) return isoDate;

  if (granularity === "day") {
    return cal(start).format("ddd M/D/YY");
  }

  if (granularity === "week") {
    const end = toDay(cal(start).add(6, "days"));
    const [from, to] = clampToRange(start, end, range);
    return formatSpan(from, to);
  }

  const monthStart = toDay(cal(start).startOf("month"));
  const monthEnd = toDay(cal(start).endOf("month"));
  const [from, to] = clampToRange(monthStart, monthEnd, range);
  const wholeMonth =
    formatLocalDate(from) === formatLocalDate(monthStart) &&
    formatLocalDate(to) === formatLocalDate(monthEnd);
  if (wholeMonth) return cal(monthStart).format("MMM YYYY");
  if (formatLocalDate(from) === formatLocalDate(to)) {
    return cal(from).format("MMM D, YYYY");
  }
  return `${cal(from).format("MMM D")} – ${cal(to).format("D, YYYY")}`;
}
